import { z } from 'zod';
import { QUOTE_LINE_KINDS } from './domain.js';
import { netUnitPriceCents } from './units.js';
export const pricingLine = z
  .object({
    key: z.string().regex(/^[a-zA-Z0-9_-]{1,64}$/),
    group: z.string().trim().max(120).default('Prestations'),
    label: z.string().trim().min(1).max(240),
    kind: z.enum(QUOTE_LINE_KINDS).default('forfait'),
    priceCents: z.number().int().min(0).max(100_000_000),
    vatBp: z.number().int().min(0).max(10000).nullable().default(null),
    frequency: z.enum(['per_visit', 'once']).default('per_visit'),
    // Remise déjà prévue (en points de base) : reprise telle quelle dans les devis,
    // factures et simulations, pour qu'aucun technicien ne l'oublie.
    discountBp: z.number().int().min(0).max(10000).default(0),
    // Prix indicatif à vérifier à chaque devis ou facture avant enregistrement.
    priceToConfirm: z.boolean().default(false),
    // Question du barème dont la réponse donne la quantité de la ligne
    // (nombre : la valeur ; oui/non : 1 ou ligne écartée). Absente : quantité 1.
    quantityFrom: z.string().regex(/^[a-zA-Z0-9_-]{1,64}$/).nullable().default(null),
  })
  .strict();
/** Question posée à la création d'un devis ou d'une facture à partir du barème. */
export const pricingQuestion = z
  .object({
    key: z.string().regex(/^[a-zA-Z0-9_-]{1,64}$/),
    label: z.string().trim().min(1).max(160),
    kind: z.enum(['number', 'yes_no']),
  })
  .strict();
export const PRICING_QUESTIONS_MAX = 10;
export const pricingLines = z
  .array(pricingLine)
  .min(1)
  .max(100)
  .refine(
    (lines) => new Set(lines.map((l) => l.key)).size === lines.length,
    'Les repères de ligne doivent être uniques.',
  );
export const pricingTemplateBody = z
  .object({
    name: z.string().trim().min(1).max(200),
    purposes: z
      .array(z.enum(['maintenance', 'quote', 'invoice']))
      .min(1)
      .max(3)
      .refine((items) => new Set(items).size === items.length),
    basis: z.enum(['ht', 'ttc', 'unspecified']),
    lines: pricingLines,
    questions: z.array(pricingQuestion).max(PRICING_QUESTIONS_MAX).default([]),
    active: z.boolean().default(true),
  })
  .strict()
  .refine(
    (v) => new Set(v.questions.map((q) => q.key)).size === v.questions.length,
    'Les repères de question doivent être uniques.',
  )
  .refine(
    (v) =>
      v.lines.every(
        (line) => line.quantityFrom === null || v.questions.some((q) => q.key === line.quantityFrom),
      ),
    'Une ligne dépend d’une question qui n’existe plus.',
  )
  .refine(
    (v) =>
      !v.purposes.some((p) => p === 'quote' || p === 'invoice') ||
      (v.basis !== 'unspecified' && v.lines.every((l) => l.vatBp !== null)),
    'Les modèles de devis/factures exigent une base HT ou TTC et un taux de TVA pour chaque ligne.',
  );
export type PricingLine = z.infer<typeof pricingLine>;
export type PricingQuestion = z.infer<typeof pricingQuestion>;
export type PricingAnswers = Record<string, number | boolean>;
/**
 * Quantité de chaque ligne d'après les réponses : une ligne sans question vaut 1,
 * une question « nombre » donne sa valeur (jamais négative), un « oui » vaut 1 et
 * un « non » écarte la ligne (0). Les barèmes antérieurs aux questions valent 1.
 */
export function pricingQuantities(
  template: { lines: readonly Pick<PricingLine, 'key' | 'quantityFrom'>[]; questions?: readonly PricingQuestion[] },
  answers: PricingAnswers,
): Record<string, number> {
  const questions = new Map((template.questions ?? []).map((q) => [q.key, q]));
  return Object.fromEntries(
    template.lines.map((line) => {
      const question = line.quantityFrom ? questions.get(line.quantityFrom) : undefined;
      if (!question) return [line.key, 1];
      const answer = answers[question.key];
      if (question.kind === 'yes_no') return [line.key, answer === true ? 1 : 0];
      const value = typeof answer === 'number' && Number.isFinite(answer) ? answer : 0;
      return [line.key, Math.max(0, Math.round(value * 1000) / 1000)];
    }),
  );
}
export interface PricingTemplate extends z.infer<typeof pricingTemplateBody> {
  id: string;
  version: number;
}
/** Prix après la remise prévue sur la ligne (anciens instantanés : sans remise). */
export function pricingNetCents(line: Pick<PricingLine, 'priceCents'> & { discountBp?: number }): number {
  return netUnitPriceCents(line.priceCents, line.discountBp ?? 0);
}
export function simulationTotals(
  lines: readonly PricingLine[],
  quantities: Record<string, number>,
  oneBp: number,
  twoBp: number,
  oneVisits = 1,
  twoVisits: number | null = 2,
) {
  const before = (visits: number) =>
    lines.reduce(
      (sum, item) =>
        sum +
        pricingNetCents(item) * (quantities[item.key] ?? 0) * (item.frequency === 'once' ? 1 : visits),
      0,
    );
  const oneBeforeCents = before(oneVisits);
  const twoBeforeCents = twoVisits === null ? 0 : before(twoVisits);
  return {
    oneBeforeCents,
    twoBeforeCents,
    oneCents: Math.round((oneBeforeCents * (10000 - oneBp)) / 10000),
    twoCents: Math.round((twoBeforeCents * (10000 - twoBp)) / 10000),
  };
}
export function pricingHtCents(line: PricingLine, basis: PricingTemplate['basis']): number {
  if (line.vatBp === null || basis === 'unspecified')
    throw new Error('Renseignez la base tarifaire et la TVA du modèle.');
  return basis === 'ttc'
    ? Math.round((line.priceCents * 10000) / (10000 + line.vatBp))
    : line.priceCents;
}
